import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { CornerLeftUpIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { RadioCard } from "simplydo/react/components/RadioCard/RadioCard";
import {
  Checkbox,
  CheckboxWhenChecked,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useAppSelector } from "store";

import { getImpactTypeIcon } from "components/lib/Ideas/ImpactStatements/impactIcons";

import type { IdeasLibUser, ImpactStatementWithCreator } from "../types";
import { ImpactStatementCategories } from "./ImpactStatementCategories";
import { mapOrganisationImpacts } from "./mapOrganisationImpacts";
import { formatOption } from "./statementUtils";
import { useImpactStatements } from "./useImpactStatements";

type ImpactStatementReadonlyProps = {
  ideaId: string;
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  rangeInputType?: "auto" | "allowCustom";
};

export const ImpactStatementReadonly = ({
  ideaId,
  stage,
  rangeInputType,
}: ImpactStatementReadonlyProps) => {
  const { t } = useTranslation();
  const user: IdeasLibUser = useAppSelector((state) => state.user);
  const organisationId = user?.ownerOrganisation?._id;
  const { impactStatements, statementDefinition } = useImpactStatements(ideaId);
  const { data: mappedImpactCategories = [] } = useQuery(
    initUseQuery(api.organisations.getImpacts, [organisationId!], {
      isEnabled: !!organisationId,
      select: mapOrganisationImpacts,
    }),
  );

  const impactStatement = impactStatements[stage] as
    | ImpactStatementWithCreator
    | undefined;

  if (!impactStatement || !statementDefinition) {
    return null;
  }

  return (
    <div key={stage}>
      <Divider hidden />
      <ImpactStatementCategories
        stage={stage}
        statementDefinition={statementDefinition}
        impactStatement={impactStatement}
        mappedImpactCategories={mappedImpactCategories}
        hideUnselectedImpacts={stage === "userEstimate"}
        renderImpact={(impact) => {
          const statementImpact = impactStatement.impacts?.[impact._id];
          const impactDef = statementDefinition.impacts?.[impact._id];

          return (
            <Checkbox
              toggle
              disabled
              style={{ marginBottom: 0 }}
              checked={statementImpact?.enabled}
            >
              <CheckboxHeader>
                {impact.icon ? (
                  <Icon icon={getImpactTypeIcon(impact.type)} />
                ) : null}
                {impact.name}
                {impact?.unit && <span> ({impact.unit})</span>}
              </CheckboxHeader>
              {impact.description ? (
                <CheckboxContent>
                  <span style={{ whiteSpace: "pre-line" }}>
                    {impact.description}
                  </span>
                </CheckboxContent>
              ) : null}
              <CheckboxWhenChecked>
                <div>
                  {impactDef?.options || impactDef?.customOptionEnabled ? (
                    <RadioCard.Group
                      editable={false}
                      defaultValue={
                        statementImpact?.choice?.custom
                          ? "__custom"
                          : (statementImpact?.choice?.id ?? "")
                      }
                    >
                      {impactDef?.options?.map((option) => {
                        const optionValue = formatOption(impact, option);
                        return (
                          <RadioCard.Item key={option.id} value={option.id}>
                            {optionValue}
                            {impact?.unit && <span> ({impact.unit})</span>}
                            {stage !== "userEstimate" &&
                            option.weight !== undefined ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "0.85em",
                                }}
                              >
                                <Icon
                                  style={{ marginRight: 2 }}
                                  icon={CornerLeftUpIcon}
                                  size="small"
                                />
                                <div>Weight: {option.weight || 0}</div>
                              </div>
                            ) : null}
                          </RadioCard.Item>
                        );
                      })}
                      {impactDef?.customOptionEnabled ||
                      rangeInputType === "allowCustom" ? (
                        <RadioCard.Item value="__custom">Other</RadioCard.Item>
                      ) : null}
                    </RadioCard.Group>
                  ) : null}
                  {statementImpact?.choice?.custom ? (
                    <>
                      <H5>Impact value</H5>
                      <p>{statementImpact?.choice?.value}</p>
                    </>
                  ) : null}
                  {impactDef?.frequencyEnabled ? (
                    <>
                      <H5>
                        {t("common:capitalise", { key: "generic.impact" })}{" "}
                        frequency
                      </H5>
                      <p>{statementImpact?.frequency}</p>
                    </>
                  ) : null}
                  <>
                    <H5>Details and justification</H5>
                    <p>{statementImpact?.justification}</p>
                  </>
                </div>
              </CheckboxWhenChecked>
            </Checkbox>
          );
        }}
      />
      <Divider hidden />
      {impactStatement?.description ? (
        <>
          <H5>Additional comments</H5>
          <p>{impactStatement.description}</p>
        </>
      ) : null}
    </div>
  );
};
